import { PenLine, Plus, Search, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/controls'
import { Input, Textarea } from '@/components/ui/field'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlays'
import { templatesSync } from '@/data/templates'
import type { MeetingNote } from '@/data/types'

type Mode = 'template' | 'write' | 'prompt'

const LABELS: Record<Mode, string> = {
  template: 'Template',
  write: 'Write it myself',
  prompt: 'Custom',
}

/**
 * One entry point for every way a note comes into being, with the three ways
 * as a mode switch rather than three stacked sections.
 *
 * Showing a template list, a title field and a prompt box at once asked the
 * reader to understand the difference between them before they could start,
 * and made the popover tall enough to cover the notes underneath it. Choosing
 * the mode first means only one set of controls is ever on screen.
 *
 * A Popover rather than a Menu: a menu captures typing for its own typeahead,
 * which makes the search field and the textarea unusable inside one.
 */
export function AddNote({
  notes,
  modes = ['template', 'write', 'prompt'],
  onAddFromTemplate,
  onAddPrompt,
  onAddBlank,
  busy,
  align = 'end',
}: {
  /**
   * What the meeting already has, which is the only thing this control needs
   * to know about it — a template already on the meeting is not offered twice.
   * Taking the notes rather than the whole meeting is what lets the external
   * share page reuse it: that page is given the notes and deliberately not the
   * meeting behind them.
   */
  notes: MeetingNote[]
  /** `write` is omitted before a meeting happens — there is nothing to write up yet. */
  modes?: Mode[]
  onAddFromTemplate: (templateId: string) => void
  onAddPrompt: (prompt: string) => void
  onAddBlank: (title: string) => void
  busy?: boolean
  align?: 'start' | 'center' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>(modes[0] ?? 'template')
  const [query, setQuery] = useState('')
  const [prompt, setPrompt] = useState('')
  const [title, setTitle] = useState('')

  const reset = () => {
    setMode(modes[0] ?? 'template')
    setQuery('')
    setPrompt('')
    setTitle('')
  }

  const close = () => {
    setOpen(false)
    reset()
  }

  const taken = new Set(notes.map((note) => note.templateId))
  const normalized = query.trim().toLowerCase()
  const templates = templatesSync().filter(
    (template) =>
      !taken.has(template.id) &&
      (!normalized ||
        template.name.toLowerCase().includes(normalized) ||
        template.description.toLowerCase().includes(normalized))
  )

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <PopoverTrigger
        disabled={busy}
        className="inline-flex h-6 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 text-2xs text-[var(--color-fg-muted)] outline-none transition-colors duration-100 hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)] disabled:opacity-40"
      >
        <Plus className="size-3" />
        Add note
      </PopoverTrigger>

      <PopoverContent align={align} className="w-80 p-0">
        {modes.length > 1 ? (
          <div className="border-b border-[var(--color-border)] p-2">
            <SegmentedControl
              size="sm"
              className="w-full [&>button]:flex-1"
              value={mode}
              onValueChange={setMode}
              options={modes.map((value) => ({ value, label: LABELS[value] }))}
            />
          </div>
        ) : null}

        {mode === 'template' ? (
          <>
            <div className="relative border-b border-[var(--color-border)]">
              <Search className="absolute top-1/2 left-2.5 size-3 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
              <Input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search templates…"
                aria-label="Search note templates"
                className="h-8 rounded-none border-0 pl-7 text-xs focus-visible:ring-0"
              />
            </div>
            <ul className="max-h-64 overflow-y-auto p-1">
              {templates.map((template) => (
                <li key={template.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onAddFromTemplate(template.id)
                      close()
                    }}
                    className="w-full rounded-[var(--radius-sm)] px-2 py-1.5 text-left hover:bg-[var(--color-surface-hover)]"
                  >
                    <span className="block truncate text-xs text-[var(--color-fg)]">
                      {template.name}
                    </span>
                    <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                      {template.description}
                    </span>
                  </button>
                </li>
              ))}
              {templates.length === 0 ? (
                <li className="px-2 py-6 text-center text-2xs text-[var(--color-fg-subtle)]">
                  {normalized ? 'No templates match.' : 'Every template is already on this meeting.'}
                </li>
              ) : null}
            </ul>
          </>
        ) : null}

        {mode === 'write' ? (
          <div className="p-2">
            <Input
              autoFocus
              value={title}
              maxLength={120}
              placeholder="Note title"
              aria-label="Note title"
              className="h-8 text-xs"
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  onAddBlank(title.trim() || 'Untitled note')
                  close()
                }
              }}
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-2xs text-[var(--color-fg-subtle)]">
                Opens an empty note to type into.
              </span>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  onAddBlank(title.trim() || 'Untitled note')
                  close()
                }}
              >
                <PenLine className="size-3.5" />
                Create
              </Button>
            </div>
          </div>
        ) : null}

        {mode === 'prompt' ? (
          <div className="p-2">
            <Textarea
              autoFocus
              rows={4}
              value={prompt}
              maxLength={2000}
              aria-label="Custom note prompt"
              placeholder="What should this note pull out of the meeting?"
              className="text-xs"
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                // The instruction is usually one sentence, so the common case
                // should not need a trip to the button. Shift+Enter breaks.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  if (prompt.trim()) {
                    onAddPrompt(prompt.trim())
                    close()
                  }
                }
              }}
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-2xs text-[var(--color-fg-subtle)]">
                A one-off instruction, not a saved template.
              </span>
              <Button
                size="sm"
                variant="primary"
                disabled={!prompt.trim()}
                onClick={() => {
                  onAddPrompt(prompt.trim())
                  close()
                }}
              >
                <Sparkles className="size-3.5" />
                Add
              </Button>
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
